import React from 'react'
import { useTranslation } from 'react-i18next'
import { connect, MqttClient } from 'mqtt'

import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Container from '@mui/material/Container'
import CircularProgress from '@mui/material/CircularProgress'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'

import Alert from '../components/alert/alert'
import { get, setPageTitle } from '../utils/service'
import { useContextHook } from '../store/context'
import LogChart from '../components/charts/logChart'
import { LegacyDatapoint, Datapoint } from '../types/datapoint'
import { LogEvent } from '../types/logEvent'
import { EventType } from '../types/enums/eventType'
import {
  calculatePredictedTemperatures,
  datapointsToSeries,
  getChartMaxTemp,
  getExpectedProfileSeries,
  getLogEvents,
  getPlotSeriesFromLog,
  getRorInterval,
  hasTurningPoint,
} from '../utils/graphUtils'
import { Sensorconfig } from '../types/sensorconfig'
import { PagedResponse } from '../types/pagedResponse'
import { Log } from '../types/log'
import { GraphSeriesConfig, GraphSeriesEvents } from '../types/graphSeriesConfig'
import { BasicDataParam, CalculatedDataParam } from '../types/enums/graphDataParam'
import { Profile } from '../types/profile'
import { MqttMessage, MqttMessageV2 } from '../types/mqttMessage'
import Comments from '../components/comments/comments'
import {
  getLogName,
  loadLogProfile,
  loadLogProfileById,
  mqttMessageToDatapoint,
  mqttMessageV2DataToDatapoint,
  legacyMqttMessageToEvent,
  legacyDatapointsToDatapoints,
  getEventsFromLogOrDatapoints,
} from '../utils/logUtils'
import TempView from '../components/live/tempView'
import LiveCompareLogs from '../components/live/liveCompareLogs'
import { Machine } from '../types/machine'
import { setCurrentMachine } from '../store/actions'
import ViewStepButtons from '../components/fields/viewStepButtons'
import LogChartTitle from '../components/logs/logChartTitle'
import BatchWeightDialog from '../components/logs/batchWeightDialog'
import { getP2000MaxWeight, getL100MaxWeight } from '../utils/weightUtils'
import InventorySelector from '../components/inventory/inventorySelector'
import { ViewStep } from '../types/enums/viewStep'
import { LEFT_COLUMN_WIDTH, RIGHT_COLUMN_WIDTH } from '../utils/theme'
import { isBtBasedProfile } from '../utils/profileEditorUtils'

enum ConnectionStatus {
  Init = 1,
  Connected = 2,
  ConnectionError = 3,
}

const LiveView = (): JSX.Element => {
  const { t } = useTranslation()
  const theme = useTheme()

  const fullscreenRef = React.useRef<HTMLElement>(null)

  const batchUuidRef = React.useRef<string>('')
  const mqttClientRef = React.useRef<MqttClient | null>(null)
  const { state, dispatch } = useContextHook()
  const [connectionStatus, setConnectionStatus] = React.useState<ConnectionStatus>(ConnectionStatus.Init)
  const [currentStep, setCurrentStep] = React.useState<ViewStep>(ViewStep.Left)
  const [profileName, setProfileName] = React.useState<string>('')
  const [profileId, setProfileId] = React.useState<number>(0)
  const [hasLoadError, setHasLoadError] = React.useState<boolean>(false)
  const [isLoading, setIsLoading] = React.useState<boolean>(false)
  const [datapoints, setDatapoints] = React.useState<Array<Datapoint>>([])
  const [events, setEvents] = React.useState<Array<LogEvent>>([])
  const [overlayProfile, setOverlayProfile] = React.useState<Profile | null>(null)
  const [batchNo, setBatchNo] = React.useState<number>(0)
  const [hasIncompleteLogLoadError, setHasIncompleteLogLoadError] = React.useState<boolean>(false)
  const [logData, setLogData] = React.useState<Log | null>(null)
  const [isNameDialogOpen, setIsNameDialogOpen] = React.useState<boolean>(false)
  const [isWeightDialogOpen, setIsWeightDialogOpen] = React.useState<boolean>(false)
  const [isInventoryDialogOpen, setIsInventoryDialogOpen] = React.useState<boolean>(false)

  const [compareSeriesConfig, setCompareSeriesConfig] = React.useState<Array<GraphSeriesConfig>>([])
  const [compareEventsConfig, setCompareEventsConfig] = React.useState<GraphSeriesEvents | null>(null)

  const isScreenBelowMdBreakpoint = useMediaQuery(theme.breakpoints.down('md'))

  const getChartStatus = (): string => {
    const hasChargeEvent = events.find((x) => x.type === EventType.Charge)
    const hasDropEvent = events.find((x) => x.type === EventType.Drop)

    if (connectionStatus === ConnectionStatus.ConnectionError) {
      return t('liveview.connectionfailed')
    } else if (connectionStatus === ConnectionStatus.Init) {
      return t('liveview.waitingforconnection')
    } else if (hasDropEvent) {
      // when we have seen drop event
      return t('liveview.roastcomplete')
    } else if (connectionStatus === ConnectionStatus.Connected && !hasChargeEvent && datapoints.length === 0) {
      // connected, waiting for the charge event
      return t('liveview.connectedtoserver')
    } else if (datapoints.length > 0) {
      // when we have seen charge event
      return t('liveview.roastinprogress')
    }
    return ''
  }

  React.useEffect(() => {
    if (state.currentMachine?.next_batch_no) {
      setBatchNo(state.currentMachine.next_batch_no)
    }
  }, [state.currentMachine?.next_batch_no])

  const getSensorconfig = (): Sensorconfig | null => {
    return state.currentMachine?.sensor_config || null
  }

  const getLogTitle = (): string => {
    const defaultLogName = getLogName({ batch_no: batchNo } as Log, '', t('liveview.batch'))
    return logData?.name || defaultLogName
  }

  const getExportFileName = (): string =>
    t('logview.csvfilename')
      .replace('{0}', state.currentMachine?.name || '')
      .replace('{1}', `${batchNo}`)

  const disconnectMqtt = (): void => {
    setDatapoints([])
    setEvents([])
    if (mqttClientRef.current) {
      mqttClientRef.current.end()
    }
  }

  React.useEffect(() => {
    disconnectMqtt()

    setIsLoading(true)
    setHasLoadError(false)
    setOverlayProfile(null)
    setLogData(null)
    setProfileName('')
    setHasIncompleteLogLoadError(false)

    setCompareSeriesConfig([])
    setCompareEventsConfig(null)

    // loading can only happen after we have loaded user (otherwise global Highchart theme might be set after live chart is rendered)
    if (state.currentMachine?.slug && state.user?.id) {
      setIsLoading(false)
      const connectionString =
        window.location.protocol === 'https:'
          ? `wss://${process.env.REACT_APP_MQTT_URL}:8083/mqtt`
          : `ws://${process.env.REACT_APP_MQTT_URL}:9001/mqtt`
      mqttClientRef.current = connect(connectionString, {
        clientId: `ROEST_${state.currentMachine.slug}_${Math.random() * 10000}`,
        keepalive: 60,
        connectTimeout: 4000,
        username: state.currentMachine?.mqtt_config.username,
        password: state.currentMachine?.mqtt_config.subscribe_password,
        clean: true,
      })
      mqttClientRef.current.on('connect', () => {
        setConnectionStatus(ConnectionStatus.Connected)
        if (mqttClientRef.current) {
          mqttClientRef.current.subscribe(state.currentMachine?.mqtt_config.topic || '')
          if (state.currentMachine?.mqtt_config.topic.startsWith('roest/s100')) {
            // The S100s can be downgraded, so we must make sure we listen for legacy packets.
            const topic = `roest/machine/particle_id/${state.currentMachine.particle_id}/#`
            mqttClientRef.current.subscribe(topic)
          }
        }
      })
      mqttClientRef.current.on('message', (_topic: string, payload: MqttMessage) => {
        const jsonMessage: MqttMessage | MqttMessageV2 = JSON.parse(payload.toString())
        console.log(jsonMessage) // for temp debugging on server

        let mqtt_data: Array<Datapoint> = []
        let mqtt_events: Array<LogEvent> = []
        let batch_uuid: string = ''
        if (jsonMessage.hasOwnProperty('events')) {
          // MQTT Log V2 -- Events is a separate array
          const mqttMessageV2 = jsonMessage as MqttMessageV2
          setProfileId(parseInt(mqttMessageV2.profile_id))
          mqtt_events = mqttMessageV2.events
          batch_uuid = mqttMessageV2.batch_uuid
          mqtt_data = [mqttMessageV2DataToDatapoint(mqttMessageV2.data)]
        } else {
          // Legacy MQTT Log -- Event data is interleaved with sensor data
          const legacyMqtt = jsonMessage as MqttMessage
          if (legacyMqtt.profile_name) {
            setProfileName(legacyMqtt.profile_name)
          }
          batch_uuid = legacyMqtt.batch_uuid.toString()
          if (legacyMqtt.data_type === 'data') {
            mqtt_data = [mqttMessageToDatapoint(legacyMqtt)]
          } else {
            mqtt_events = [legacyMqttMessageToEvent(legacyMqtt)]
          }
        }
        if (batchUuidRef.current !== batch_uuid) {
          // New batch
          batchUuidRef.current = batch_uuid

          if (state.currentMachine?.url) {
            get<Machine>(state.currentMachine.url).then((machineResponse) => {
              dispatch(setCurrentMachine(machineResponse.data))
            })
          }

          setEvents([])
          setDatapoints([])
          loadIncompleteLog(batch_uuid)
        }
        setEvents((oldEvents) => {
          // For legacy packets mqtt_events is either empty or a single event, so it must be appended.
          return jsonMessage.hasOwnProperty('events') ? mqtt_events : [...oldEvents, ...mqtt_events]
        })
        setDatapoints((oldDatapoints) => [...oldDatapoints, ...mqtt_data])
      })
      mqttClientRef.current.on('close', () => {
        // client.on('error', (err) => ...) does not work. Assume that on close, there is an error
        setConnectionStatus(ConnectionStatus.ConnectionError)
      })
      mqttClientRef.current.on('end', () => {
        // this is called after close event, when we end the connection ourselves with client.end()
        setConnectionStatus(ConnectionStatus.Init)
      })
    }

    return () => {
      disconnectMqtt()
    }
  }, [
    dispatch,
    state.user?.id,
    state.currentMachine?.url,
    state.currentMachine?.slug,
    state.currentMachine?.particle_id,
    state.currentMachine?.mqtt_config.username,
    state.currentMachine?.mqtt_config.topic,
    state.currentMachine?.mqtt_config.subscribe_password,
  ]) // subscribe to mqtt when mqtt config is changed

  React.useEffect(() => {
    if (state.currentCustomer?.id) {
      const loadProfileCallback = (profile: Profile | null): void => {
        if (profile && !isBtBasedProfile(profile.profile_type)) {
          setOverlayProfile(profile)
          if (!profileName || profileName !== profile.name) {
            setProfileName(profile.name)
          }
        }
      }
      if (profileId === 0 && profileName) {
        setOverlayProfile(null) // reset roast profile when profileName is changed
        // when we have a profile name, try to load this profile
        loadLogProfile(
          profileName,
          state.currentCustomer.id,
          (profile: Profile | null) => loadProfileCallback(profile),
          () => {}
        )
      } else if (profileId) {
        setOverlayProfile(null) // reset roast profile when profileName is changed
        // when we have a profile id, try to load this profile
        loadLogProfileById(
          profileId,
          (profile: Profile | null) => loadProfileCallback(profile),
          () => {}
        )
      }
    }
  }, [profileName, profileId, state.currentCustomer?.id])

  // update page title
  React.useEffect(() => {
    if (state.currentMachine?.name) {
      setPageTitle(`${state.currentMachine?.name} ${t('liveview.live')}`)
    }
  }, [state.currentMachine?.name, t])

  const generatePrediction = (): Array<GraphSeriesConfig> => {
    const CALC_INTERVAL = 5 // calculate new prediction every CALC_INTERVAL seconds
    const sliceEnd = datapoints.length - (datapoints.length % CALC_INTERVAL)
    const calcDatapoints = datapoints.slice(0, sliceEnd)
    const chartSeries = getPlotSeriesFromLog(calcDatapoints, getSensorconfig(), theme, state.user)
    const plotSeries = chartSeries.filter((x) => x.param === BasicDataParam.BT || x.param === BasicDataParam.ET)
    const hasTp = hasTurningPoint(chartSeries.find((x) => x.param === CalculatedDataParam.ROR)?.datapoints || [])

    return calculatePredictedTemperatures(plotSeries, hasTp, theme)
  }

  const calculateEventDatapoints = (): Array<GraphSeriesEvents> => {
    const btPoints = datapointsToSeries(datapoints, BasicDataParam.BT)
    const eventSeries = [getLogEvents(events, null, '', btPoints)]
    if (compareEventsConfig) {
      eventSeries.push(compareEventsConfig)
    }
    return eventSeries
  }

  const loadIncompleteLog = (batchUuidToLoad: string): void => {
    get<PagedResponse<Log>>(`logs/?batch_uuid=${batchUuidToLoad}`).then((logResult) => {
      if (logResult.data.results.length > 0) {
        get<Array<LegacyDatapoint>>(`datapoints/?page_size=all&log=${logResult.data.results[0].id}`)
          .then((datapointsResult) => {
            const log = logResult.data.results[0]
            setLogData(log)
            setEvents((oldEvents) => {
              const receivedEvents = getEventsFromLogOrDatapoints(log, datapointsResult.data).filter(
                (x) => oldEvents.length === 0 || x.msec < oldEvents[0].msec
              )
              return [...receivedEvents, ...oldEvents].sort((x, y) => x.msec - y.msec)
            })
            setDatapoints((oldDatapoints) => {
              const receivedDatapoints = legacyDatapointsToDatapoints(datapointsResult.data).filter(
                (x) => oldDatapoints.length === 0 || x.msec < oldDatapoints[0].msec
              )
              return [...receivedDatapoints, ...oldDatapoints].sort((x, y) => x.msec - y.msec)
            })
          })
          .catch(() => setHasIncompleteLogLoadError(true))
      }
    })
  }

  const shouldShowStep = (step: ViewStep) => {
    return !isScreenBelowMdBreakpoint || (isScreenBelowMdBreakpoint && currentStep === step)
  }

  return (
    <Container
      component="main"
      maxWidth={false}
      sx={{ display: 'flex', flex: 1, overflow: 'hidden', flexDirection: 'column' }}
    >
      <Stack direction="row" sx={{ flex: 1, overflow: 'hidden' }}>
        {hasLoadError && <Alert severity="error">{t('liveview.loaderror')}</Alert>}
        {hasIncompleteLogLoadError && <Alert severity="error">{t('liveview.incompletelogloaderror')}</Alert>}
        {isLoading && <CircularProgress />}
        {isNameDialogOpen && logData && (
          <LogChartTitle
            title={getLogTitle()}
            logUrl={logData.url}
            onClose={() => setIsNameDialogOpen(false)}
            onTitleChanged={(updatedLogData: Log) => {
              setLogData(updatedLogData)
              setIsNameDialogOpen(false)
            }}
          />
        )}
        {isWeightDialogOpen && logData && state.currentMachine && (
          <BatchWeightDialog
            log={logData}
            maxBatchWeightInGrams={state.currentMachine.is_p2000 ? getP2000MaxWeight() : getL100MaxWeight()}
            onClose={() => setIsWeightDialogOpen(false)}
            onSaveSuccess={(newLogData: Log) => {
              setLogData(newLogData)
              setIsWeightDialogOpen(false)
            }}
          />
        )}
        {isInventoryDialogOpen && logData && state.currentCustomer && (
          <InventorySelector
            customer={state.currentCustomer}
            log={logData}
            disableArchivedInventory
            onClose={() => setIsInventoryDialogOpen(false)}
            onSaveSuccess={(newLogData: Log) => {
              setLogData(newLogData)
              setIsInventoryDialogOpen(false)
            }}
          />
        )}
        <Stack
          ref={fullscreenRef}
          className="live-fullscreen"
          direction="row"
          sx={{ height: '100%', flex: 1, overflow: 'hidden', backgroundColor: 'background.default' }}
        >
          {/* step 1 */}
          {shouldShowStep(ViewStep.Left) && state.currentMachine && !isLoading && (
            <Stack width={isScreenBelowMdBreakpoint ? '100%' : LEFT_COLUMN_WIDTH}>
              <TempView
                events={events}
                status={getChartStatus()}
                profile={profileName}
                profileId={profileId}
                machineSlug={state.currentMachine?.slug}
                logName={getLogTitle()}
                start_weight={logData?.start_weight || 0}
                end_weight={logData?.end_weight || 0}
                inventoryId={logData?.inventory_id || 0}
                beanName={logData?.inventory_name || logData?.bean_name || ''}
                seriesConfig={getPlotSeriesFromLog(datapoints, getSensorconfig(), theme, state.user)}
                compareSeriesConfig={compareSeriesConfig}
                onSetName={logData ? () => setIsNameDialogOpen(true) : undefined}
                onSetWeight={logData ? () => setIsWeightDialogOpen(true) : undefined}
                onSetBean={logData ? () => setIsInventoryDialogOpen(true) : undefined}
              />
            </Stack>
          )}
          {/* step 2 */}
          {shouldShowStep(ViewStep.Center) && state.currentMachine && !isLoading && (
            <Stack sx={{ height: '100%', flex: 1, overflow: 'hidden', mr: isScreenBelowMdBreakpoint ? 0 : 2 }}>
              <LogChart
                chartMaxTemp={getChartMaxTemp(state.user, state.currentMachine)}
                exportTitle={getExportFileName()}
                seriesConfig={getPlotSeriesFromLog(datapoints, getSensorconfig(), theme, state?.user)}
                eventDatapoints={calculateEventDatapoints()}
                compareLogSeries={compareSeriesConfig}
                expectedSeries={[...getExpectedProfileSeries(theme, overlayProfile), ...generatePrediction()]}
                livePlotlineMsec={datapoints.length > 0 ? datapoints[datapoints.length - 1].msec : undefined}
                rorInterval={getRorInterval(state?.user)}
                storeSeriesVisiblity={true}
                onRequestFullscreen={() => {
                  if (fullscreenRef.current) {
                    fullscreenRef.current.requestFullscreen()
                  }
                }}
              />
            </Stack>
          )}
        </Stack>
        {/* step 3 */}
        {shouldShowStep(ViewStep.Right) && state.currentMachine && !isLoading && (
          <Stack spacing={1} sx={{ overflow: 'auto', width: isScreenBelowMdBreakpoint ? '100%' : RIGHT_COLUMN_WIDTH }}>
            <LiveCompareLogs
              currentMachine={state.currentMachine}
              user={state.user}
              sensorconfig={getSensorconfig()}
              onSetCompareSeriesConfig={setCompareSeriesConfig}
              onSetCompareEventsConfig={setCompareEventsConfig}
            />
            {logData && (
              <Accordion disableGutters defaultExpanded={true}>
                <AccordionSummary sx={{ backgroundColor: 'secondary.light' }} expandIcon={<ExpandMoreIcon />}>
                  <Typography>{t('logview.comments')}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Comments logId={logData.id} logUrl={logData.url} user={state.user} />
                </AccordionDetails>
              </Accordion>
            )}
          </Stack>
        )}
      </Stack>
      {isScreenBelowMdBreakpoint && (
        <ViewStepButtons
          steps={[ViewStep.Left, ViewStep.Center, ViewStep.Right]}
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
        />
      )}
    </Container>
  )
}

export default LiveView
